// src/services/aeo/trafficMarkers.ts
//
// Outside events staff put on the traffic chart, such as a sale or an ad
// campaign (ourteam-backend#6426). Work Oura published is marked by the
// backend from the publication record and cannot be added or removed here.
// The markers themselves arrive on the traffic block (`traffic.markers`), so
// these two calls only write; the caller reloads the payload after.
//
//   addTrafficMarker(businessId, date, label)    POST   /businesses/{id}/traffic-markers
//   removeTrafficMarker(businessId, markerId)    DELETE /businesses/{id}/traffic-markers/{markerId}
//
// Nothing here throws: a refusal comes back as `{ok: false, message}` with the
// server's sentence, which is the one the card shows.

import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
const AEO_BASE = `${apiURL}/api/v1`;

/** The server's limit on a label, in characters. */
export const MARKER_LABEL_MAX = 120;

export type MarkerResult = { ok: true } | { ok: false; message: string };

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
}

function refusal(err: unknown, where: string): MarkerResult {
    const res = (err as { response?: { status?: number; data?: { message?: string } } }).response;
    if (!res) return { ok: false, message: "The server did not answer. Check your connection and try again." };
    if (!res.data?.message) catchErrorLog(`[oura] traffic marker ${where} failed (${res.status ?? "no status"})`);
    return { ok: false, message: res.data?.message ?? "That did not go through. Try again." };
}

const markersUrl = (businessId: string) => `${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/traffic-markers`;

export async function addTrafficMarker(businessId: string, date: string, label: string): Promise<MarkerResult> {
    try {
        await axios.post(markersUrl(businessId), { date, label }, { headers: authHeaders() });
        return { ok: true };
    } catch (err) {
        return refusal(err, "add");
    }
}

export async function removeTrafficMarker(businessId: string, markerId: string): Promise<MarkerResult> {
    try {
        await axios.delete(`${markersUrl(businessId)}/${encodeURIComponent(markerId)}`, { headers: authHeaders() });
        return { ok: true };
    } catch (err) {
        return refusal(err, "remove");
    }
}
